/**
 * PSP/CRE Tool v2.0 - Operational Postures Configuration
 * 
 * Purpose: Define operational postures and their effects on casualties,
 * medical operations, evacuation paradigms, and MTF status.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 24, 2024
 * Version: 2.2 - Added posture normalization for Tab 2 object handling
 * 
 * v2.2 CHANGES (January 21, 2026):
 * - BUG-045 FIX: Added normalizePostureValue() helper function
 * - Tab 2 saves posture as object {value: 'X', label: 'Y'} but functions expect string 'X'
 * - getPostureByValue() now normalizes input before lookup
 * - All helper functions protected against object postures
 * - Exported normalizePostureValue for use by other modules
 * - STANDARDIZED: All PSP/CRE modules now use DELIBERATE_DEFENSE as default
 * - (Previously this file used HASTY_DEFENSE, now consistent with other modules)
 * 
 * v2.1 CHANGES:
 * - Added SUPPORT_OPERATIONS posture for logistics, medical, signal units
 * - Support units now have appropriate posture (not HASTY_DEFENSE)
 * - 0.05x casualty multiplier (rear area, not in contact)
 * - Supports modular architecture for any nation
 * 
 * References:
 * - NATO AJMedP-1 Allied Joint Medical Support Doctrine
 * - NATO AJP-4.10 Allied Joint Doctrine for Medical Support
 * - FM 3-0 Operations
 * - FM 4-02 Army Health System
 * - Ukraine 2022-2024 combat data (RUSI, ISW, Oryx)
 * - Romanian Army operational doctrine
 * 
 * Key Concepts:
 * - Posture determines base casualty multiplier
 * - Posture affects medical facility operational status
 * - Posture determines evacuation paradigm (PERMISSIVE/CONTESTED/DENIED)
 * - Nested postures: Battalion posture overrides Brigade overrides Division
 * 
 * Usage:
 * import { 
 *   OPERATIONAL_POSTURES,
 *   getPostureCasualtyMultiplier,
 *   getEvacuationParadigm,
 *   calculatePostureModifier
 * } from '../constants/postures';
 */

// ============================================================================
// POSTURE CATEGORIES
// ============================================================================

export const POSTURE_CATEGORIES = {
  OFFENSIVE: {
    label: 'Offensive Operations',
    description: 'Operations to seize, retain, and exploit the initiative',
    typical_casualty_range: { min: 0.9, max: 1.4 },
    medical_considerations: [
      'Medical assets must keep pace with advancing forces',
      'Evacuation routes lengthen as advance progresses',
      'Supply lines extend, increasing vulnerability',
      'Forward surgical capability essential'
    ]
  },
  DEFENSIVE: {
    label: 'Defensive Operations',
    description: 'Operations to defeat enemy attack, gain time, or hold terrain',
    typical_casualty_range: { min: 0.7, max: 1.0 },
    medical_considerations: [
      'Medical facilities in prepared positions',
      'Evacuation routes established and protected',
      'Supply lines shorter and more secure',
      'Holding capacity may be needed'
    ]
  },
  RETROGRADE: {
    label: 'Retrograde Operations',
    description: 'Organized movement away from the enemy',
    typical_casualty_range: { min: 0.9, max: 1.4 },
    medical_considerations: [
      'Medical assets must withdraw in sequence',
      'Casualty evacuation challenging during movement',
      'May need to evacuate patients from facilities',
      'Prolonged field care likely'
    ]
  },
  STABILITY: {
    label: 'Stability Operations',
    description: 'Operations to maintain or establish security and order',
    typical_casualty_range: { min: 0.1, max: 0.5 },
    medical_considerations: [
      'Lower combat casualties, higher DNBI',
      'Civilian medical support may be required',
      'Host nation medical integration',
      'Mental health support important'
    ]
  },
  TRANSITION: {
    label: 'Transition/Movement',
    description: 'Movement between positions or phases',
    typical_casualty_range: { min: 0.8, max: 1.3 },
    medical_considerations: [
      'Medical facilities may be displacing',
      'Reduced capability during movement',
      'Higher risk of ambush casualties',
      'Coordination critical'
    ]
  },
  NON_COMBAT: {
    label: 'Non-Combat/Reserve',
    description: 'Out of contact, reconstituting, or in reserve',
    typical_casualty_range: { min: 0.0, max: 0.1 },
    medical_considerations: [
      'DNBI only (no combat casualties)',
      'Medical assets available to support others',
      'Opportunity for medical training',
      'Equipment maintenance'
    ]
  }
};

// ============================================================================
// OPERATIONAL POSTURES - OFFENSIVE
// ============================================================================

export const OFFENSIVE_POSTURES = {
  DELIBERATE_ATTACK: {
    value: 'DELIBERATE_ATTACK',
    label: 'Deliberate Attack',
    icon: '⚔️',
    category: 'OFFENSIVE',
    
    // Casualty multiplier (1.0 = baseline)
    casualty_multiplier: 0.9,
    
    // Ukraine-validated data
    ukraine_data: {
      observed_multiplier: 0.85,
      sample_operations: ['Kherson offensive Aug-Nov 2022', 'Kharkiv Sep 2022'],
      notes: 'Prepared attacks had lower casualties than expected',
      key_factor: 'Artillery preparation reduces assault casualties'
    },
    
    // Description
    description: 'Prepared offensive operation with thorough planning and reconnaissance',
    
    // Medical operational status
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      // Evacuation paradigm
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Pre-positioned medical assets, rehearsed evacuation routes',
      
      // Golden hour achievability
      golden_hour_probability: 0.65,
      
      // Medical positioning
      mtf_positioning: 'Optimized based on plan',
      supply_status: 'Full stocks forward',
      blood_forward: true,
      
      // Capacity considerations
      expected_flow: 'Planned, manageable',
      mascal_risk: 'LOW',
      pfc_likely: false
    },
    
    // Characteristics
    characteristics: [
      'Thorough planning and preparation (24-72 hours)',
      'Detailed reconnaissance',
      'Rehearsals conducted',
      'Medical assets pre-positioned',
      'Evacuation routes reconnoitered',
      'Blood products forward',
      'Artillery/air preparation'
    ],
    
    // Timing factors
    timing: {
      preparation_time_hours: { min: 24, typical: 48, max: 72 },
      sustainability_days: { min: 3, typical: 7, max: 14 }
    },
    
    // Romanian context
    romanian_term: 'Atac Deliberat',
    
    // Teaching points
    teaching_points: [
      'Lower casualty rate due to preparation',
      'Medical system optimally configured',
      'Best chance for golden hour achievement',
      'Evacuation routes tested',
      'Medical staff rested and prepared'
    ],
    
    // Ukraine 2022-2024 lessons
    ukraine_lessons: [
      'Artillery preparation still causes 50-60% of enemy casualties',
      'Drone reconnaissance improves medical positioning',
      'Electronic warfare affects medical communications',
      'Mines cause 15-20% casualties even with preparation',
      'Night attacks reduce drone vulnerability'
    ]
  },
  
  HASTY_ATTACK: {
    value: 'HASTY_ATTACK',
    label: 'Hasty Attack',
    icon: '⚔️',
    category: 'OFFENSIVE',
    
    casualty_multiplier: 1.2,
    
    ukraine_data: {
      observed_multiplier: 1.3,
      notes: 'Hasty attacks often costly without proper preparation',
      key_factor: 'Lack of artillery preparation increases assault casualties'
    },
    
    description: 'Immediate attack to exploit opportunity or maintain momentum',
    
    medical: {
      operational: true,
      mtf_status: 'OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Evacuation routes may not be optimal, medical assets catching up',
      
      golden_hour_probability: 0.40,
      
      mtf_positioning: 'Based on current locations',
      supply_status: 'What units carry',
      blood_forward: false,
      
      expected_flow: 'Unpredictable, potentially high',
      mascal_risk: 'MODERATE',
      pfc_likely: true
    },
    
    characteristics: [
      'Conducted with minimal preparation (2-6 hours)',
      'Exploits fleeting opportunity',
      'Medical assets may lag',
      'Evacuation routes hasty',
      'Higher risk of mass casualties',
      'Communication challenges'
    ],
    
    timing: {
      preparation_time_hours: { min: 2, typical: 4, max: 6 },
      sustainability_days: { min: 1, typical: 2, max: 3 }
    },
    
    romanian_term: 'Atac din Mers',
    
    teaching_points: [
      'Higher casualty rate (20% increase)',
      'Medical system less prepared',
      'Golden hour more difficult',
      'Prolonged Field Care often required',
      'Medical assets must be agile'
    ],
    
    medical_challenges: [
      'MTFs may not be optimally positioned',
      'Evacuation distances longer',
      'Blood products may not be forward',
      'Staff fatigue from movement',
      'Equipment may not be fully set up'
    ]
  },
  
  EXPLOITATION: {
    value: 'EXPLOITATION',
    label: 'Exploitation',
    icon: '➡️',
    category: 'OFFENSIVE',
    
    casualty_multiplier: 0.8,
    
    ukraine_data: {
      observed_multiplier: 0.75,
      sample_operations: ['Kharkiv exploitation Sep 2022'],
      notes: 'Successful exploitation has low casualties - enemy disorganized'
    },
    
    description: 'Follow-on operation to capitalize on success',
    
    medical: {
      operational: true,
      mtf_status: 'OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Rapidly extending lines challenge evacuation',
      
      golden_hour_probability: 0.35,
      
      mtf_positioning: 'Catching up with advance',
      supply_status: 'Extended lines',
      blood_forward: false,
      
      expected_flow: 'Low combat, high DNBI',
      mascal_risk: 'LOW',
      pfc_likely: true
    },
    
    characteristics: [
      'Follow-up to successful attack',
      'Enemy disorganized/retreating',
      'Rapid movement',
      'Extended supply lines',
      'Prisoner handling load'
    ],
    
    romanian_term: 'Exploatarea Succesului',
    
    teaching_points: [
      'Lower combat casualties',
      'Increased DNBI from tempo',
      'Evacuation distances extend rapidly',
      'Medical sustainment challenging',
      'EPW medical care required'
    ]
  },
  
  PURSUIT: {
    value: 'PURSUIT',
    label: 'Pursuit',
    icon: '🏃',
    category: 'OFFENSIVE',
    
    casualty_multiplier: 0.7,
    
    ukraine_data: {
      observed_multiplier: 0.65,
      notes: 'Pursuit casualties low for pursuer, very high for pursued'
    },
    
    description: 'Aggressive follow-up of retreating enemy',
    
    medical: {
      operational: true,
      mtf_status: 'STRESSED',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Very long evacuation distances, medical assets far behind',
      
      golden_hour_probability: 0.25,
      
      expected_flow: 'Low friendly casualties',
      mascal_risk: 'LOW',
      pfc_likely: true
    },
    
    characteristics: [
      'Aggressive follow-up',
      'Enemy in retreat/rout',
      'Very extended lines',
      'Medical assets far behind',
      'High EPW casualty load'
    ],
    
    romanian_term: 'Urmărirea',
    
    teaching_points: [
      'Lowest friendly combat casualties',
      'High enemy/EPW casualties',
      'Medical sustainment critical challenge',
      'Golden hour rarely achievable',
      'PFC essential capability'
    ]
  },
  
  MOVEMENT_TO_CONTACT: {
    value: 'MOVEMENT_TO_CONTACT',
    label: 'Movement to Contact',
    icon: '🔍',
    category: 'OFFENSIVE',
    
    casualty_multiplier: 1.1,
    
    ukraine_data: {
      observed_multiplier: 1.15,
      notes: 'Initial contact often costly - mines and ambushes'
    },
    
    description: 'Advance to gain or regain contact with enemy',
    
    medical: {
      operational: true,
      mtf_status: 'OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Evacuation routes being established',
      
      golden_hour_probability: 0.45,
      
      expected_flow: 'Unpredictable until contact',
      mascal_risk: 'MODERATE',
      pfc_likely: false
    },
    
    characteristics: [
      'Advancing to find enemy',
      'Contact location unknown',
      'Lead elements at high risk',
      'Mine/IED threat high',
      'Ambush possibility'
    ],
    
    romanian_term: 'Marș de Apropiere',
    
    teaching_points: [
      'Higher casualty rate until contact established',
      'Lead element (recon/cavalry) casualties',
      'Mine/IED casualties significant',
      'Medical positioning based on expected contact',
      'Flexibility required'
    ]
  }
};

// ============================================================================
// OPERATIONAL POSTURES - DEFENSIVE
// ============================================================================

export const DEFENSIVE_POSTURES = {
  DELIBERATE_DEFENSE: {
    value: 'DELIBERATE_DEFENSE',
    label: 'Deliberate Defense',
    icon: '🛡️',
    category: 'DEFENSIVE',
    
    casualty_multiplier: 0.7,
    
    ukraine_data: {
      observed_multiplier: 0.65,
      sample_operations: ['Bakhmut defense 2022-2023', 'Surovikin Line'],
      notes: 'Prepared positions dramatically reduce defender casualties',
      key_factor: 'Fortifications provide 50%+ casualty reduction'
    },
    
    description: 'Defense from prepared positions with fields of fire and obstacles',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Protected evacuation routes, optimal MTF positioning',
      
      golden_hour_probability: 0.80,
      
      mtf_positioning: 'Optimal, protected positions',
      supply_status: 'Stockpiled',
      blood_forward: true,
      
      expected_flow: 'Predictable based on enemy attack',
      mascal_risk: 'LOW',
      pfc_likely: false
    },
    
    characteristics: [
      'Prepared fighting positions (48-96 hours prep)',
      'Obstacles and minefields',
      'Fields of fire cleared',
      'Medical bunkers constructed',
      'Protected evacuation routes',
      'Supply caches established',
      'Alternate positions prepared'
    ],
    
    timing: {
      preparation_time_hours: { min: 48, typical: 72, max: 96 },
      sustainability_days: { min: 14, typical: 30, max: 90 }
    },
    
    romanian_term: 'Apărare Pregătită',
    
    teaching_points: [
      'Lowest casualty rate (30% reduction)',
      'Medical system optimally configured',
      'Best golden hour achievement',
      'Protected evacuation',
      'Hardened medical facilities',
      'Defender advantage significant'
    ],
    
    medical_advantages: [
      'MTFs in hardened positions',
      'Protected from direct fire',
      'Covered evacuation routes',
      'Supply lines secure',
      'Communication systems redundant',
      'Medical staff well-rested'
    ],
    
    ukraine_lessons: [
      'Trenches and bunkers critical for survival',
      'Medical bunkers save lives',
      'Counter-battery still dangerous',
      'Drone observation constant - camouflage essential',
      'Rotation possible with prepared positions'
    ]
  },
  
  HASTY_DEFENSE: {
    value: 'HASTY_DEFENSE',
    label: 'Hasty Defense',
    icon: '🛡️',
    category: 'DEFENSIVE',
    
    casualty_multiplier: 0.85,
    
    ukraine_data: {
      observed_multiplier: 0.9,
      notes: 'Quick positions still provide significant protection'
    },
    
    description: 'Quick defensive setup to counter enemy attack or consolidate gains',
    
    medical: {
      operational: true,
      mtf_status: 'OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Evacuation routes hasty, medical positions not optimized',
      
      golden_hour_probability: 0.55,
      
      mtf_positioning: 'Best available positions',
      supply_status: 'What units carry',
      blood_forward: false,
      
      expected_flow: 'Moderate',
      mascal_risk: 'MODERATE',
      pfc_likely: false
    },
    
    characteristics: [
      'Quick occupation of positions (2-6 hours)',
      'Limited preparation time',
      'Use of natural obstacles',
      'Medical positions hasty',
      'Evacuation routes expedient'
    ],
    
    timing: {
      preparation_time_hours: { min: 2, typical: 4, max: 6 },
      sustainability_days: { min: 1, typical: 3, max: 7 }
    },
    
    romanian_term: 'Apărare din Mers',
    
    teaching_points: [
      'Still provides significant protection',
      'Medical positions expedient',
      'Golden hour achievable with effort',
      'Transition to deliberate when possible',
      'Natural terrain features critical'
    ]
  },
  
  AREA_DEFENSE: {
    value: 'AREA_DEFENSE',
    label: 'Area Defense',
    icon: '🏰',
    category: 'DEFENSIVE',
    
    casualty_multiplier: 0.75,
    
    ukraine_data: {
      observed_multiplier: 0.7,
      sample_operations: ['Bakhmut urban defense'],
      notes: 'Urban area defense particularly effective'
    },
    
    description: 'Defense of specific terrain through mutual support of positions',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Multiple evacuation routes, dispersed medical',
      
      golden_hour_probability: 0.75,
      
      expected_flow: 'Moderate, distributed',
      mascal_risk: 'LOW',
      pfc_likely: false
    },
    
    characteristics: [
      'Terrain retention focus',
      'Mutual support between positions',
      'Limited counterattack',
      'Multiple medical points',
      'Distributed evacuation'
    ],
    
    romanian_term: 'Apărarea de Zonă',
    
    teaching_points: [
      'Multiple medical support points',
      'Distributed casualty load',
      'Evacuation from multiple directions',
      'Good sustainability',
      'Civilian consideration in urban'
    ]
  },
  
  MOBILE_DEFENSE: {
    value: 'MOBILE_DEFENSE',
    label: 'Mobile Defense',
    icon: '🔄',
    category: 'DEFENSIVE',
    
    casualty_multiplier: 0.95,
    
    ukraine_data: {
      observed_multiplier: 1.0,
      notes: 'Mobile defense challenging for medical support'
    },
    
    description: 'Defense through maneuver and counterattack',
    
    medical: {
      operational: true,
      mtf_status: 'OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Medical assets must be mobile, evacuation routes change',
      
      golden_hour_probability: 0.45,
      
      expected_flow: 'Variable based on counterattack',
      mascal_risk: 'MODERATE',
      pfc_likely: true
    },
    
    characteristics: [
      'Counterattack-focused defense',
      'Mobile reserve critical',
      'Medical assets must relocate',
      'Evacuation routes shift',
      'Communication critical'
    ],
    
    romanian_term: 'Apărarea Mobilă',
    
    teaching_points: [
      'Medical assets must be mobile',
      'Counterattack casualties in addition to defense',
      'Evacuation routes change frequently',
      'Communication essential',
      'PFC capability required'
    ]
  }
};

// ============================================================================
// OPERATIONAL POSTURES - RETROGRADE
// ============================================================================

export const RETROGRADE_POSTURES = {
  DELAY: {
    value: 'DELAY',
    label: 'Delay',
    icon: '⏪',
    category: 'RETROGRADE',
    
    casualty_multiplier: 0.9,
    
    ukraine_data: {
      observed_multiplier: 0.85,
      notes: 'Controlled delay maintains capability'
    },
    
    description: 'Trading space for time while maintaining contact',
    
    medical: {
      operational: true,
      mtf_status: 'OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Phased withdrawal of medical assets, maintained capability',
      
      golden_hour_probability: 0.50,
      
      expected_flow: 'Moderate',
      mascal_risk: 'MODERATE',
      pfc_likely: true
    },
    
    characteristics: [
      'Controlled withdrawal',
      'Successive positions',
      'Medical assets leap-frog',
      'Maintains capability throughout',
      'Covered evacuation routes'
    ],
    
    romanian_term: 'Întârziere',
    
    teaching_points: [
      'Controlled retrograde operation',
      'Medical maintains capability',
      'Leap-frog positioning',
      'Lower casualties than hasty withdrawal'
    ]
  },
  
  WITHDRAWAL: {
    value: 'WITHDRAWAL',
    label: 'Withdrawal (Under Pressure)',
    icon: '⬅️',
    category: 'RETROGRADE',
    
    casualty_multiplier: 1.1,
    
    ukraine_data: {
      observed_multiplier: 1.2,
      sample_operations: ['Kherson withdrawal Nov 2022'],
      notes: 'Successful withdrawal requires covering force'
    },
    
    description: 'Disengagement from enemy contact under pressure',
    
    medical: {
      operational: true,
      mtf_status: 'STRESSED',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'CONTESTED',
      evacuation_notes: 'Difficult evacuation, may need to abandon equipment',
      
      golden_hour_probability: 0.30,
      
      expected_flow: 'HIGH',
      mascal_risk: 'HIGH',
      pfc_likely: true
    },
    
    characteristics: [
      'Disengagement under pressure',
      'Higher risk of casualties',
      'Medical assets vulnerable',
      'May need to abandon non-critical equipment',
      'Evacuation challenging'
    ],
    
    romanian_term: 'Retragere',
    
    teaching_points: [
      'Higher casualties (10% increase)',
      'Medical system stressed',
      'Difficult evacuation',
      'May need to prioritize personnel over equipment',
      'PFC critical for unevacuated casualties'
    ],
    
    ukraine_lessons: [
      'Orderly withdrawal (Kherson) preserved force',
      'Panic withdrawal catastrophic',
      'Covering force essential',
      'Medical priority list critical',
      'Pre-planned evacuation routes'
    ]
  },
  
  RETIREMENT: {
    value: 'RETIREMENT',
    label: 'Retirement (Not Under Pressure)',
    icon: '🔙',
    category: 'RETROGRADE',
    
    casualty_multiplier: 0.3,
    
    description: 'Orderly withdrawal not under enemy pressure',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Orderly movement, medical maintains capability',
      
      golden_hour_probability: 0.70,
      
      expected_flow: 'Low (DNBI only)',
      mascal_risk: 'LOW',
      pfc_likely: false
    },
    
    characteristics: [
      'Not under enemy contact',
      'Orderly movement',
      'Medical fully operational',
      'Administrative movement possible'
    ],
    
    romanian_term: 'Retragere Administrativă',
    
    teaching_points: [
      'Minimal combat casualties',
      'DNBI primary concern',
      'Medical maintains full capability',
      'Good opportunity for rest/refit'
    ]
  }
};

// ============================================================================
// OPERATIONAL POSTURES - STABILITY/OTHER
// ============================================================================

export const STABILITY_POSTURES = {
  STABILITY_OPERATIONS: {
    value: 'STABILITY_OPERATIONS',
    label: 'Stability Operations',
    icon: '🕊️',
    category: 'STABILITY',
    
    casualty_multiplier: 0.2,
    
    description: 'Operations to maintain security and civil order',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Full capability, may support civilians',
      
      golden_hour_probability: 0.90,
      
      expected_flow: 'Low (DNBI primary)',
      mascal_risk: 'LOW',
      pfc_likely: false,
      
      civilian_support: true
    },
    
    characteristics: [
      'Low combat intensity',
      'DNBI primary concern',
      'Civilian medical support possible',
      'Mental health important',
      'Host nation integration'
    ],
    
    romanian_term: 'Operații de Stabilizare',
    
    teaching_points: [
      'Very low combat casualties',
      'DNBI and mental health primary concerns',
      'May provide civilian medical support',
      'Cultural sensitivity important',
      'Force protection still required'
    ]
  },
  
  // v2.1: NEW - Support Operations posture for CSS units
  SUPPORT_OPERATIONS: {
    value: 'SUPPORT_OPERATIONS',
    label: 'Support Operations (CSS)',
    icon: '📦',
    category: 'STABILITY',
    
    // Very low casualty rate - not in direct combat
    // BUT: Modern warfare (Ukraine 2022-2024) shows CSS units ARE targeted
    casualty_multiplier: 0.05,
    
    ukraine_data: {
      observed_multiplier: 0.08,
      notes: 'CSS units targeted by drones, artillery, long-range fires',
      key_factor: 'Rear area is NOT safe in modern warfare',
      sample_incidents: ['Logistics convoys', 'Fuel depots', 'Medical units']
    },
    
    description: 'Combat Service Support operations - logistics, medical, signal, maintenance units actively supporting engaged forces',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Medical units providing support, not receiving significant casualties',
      
      golden_hour_probability: 0.95,
      
      expected_flow: 'Minimal (DNBI primary, occasional combat)',
      mascal_risk: 'LOW',
      pfc_likely: false,
      
      // Support units provide medical capability, don't consume it
      provides_medical_support: true,
      can_support_others: true
    },
    
    characteristics: [
      'Not in direct enemy contact',
      'Rear area operations',
      'Subject to drone/artillery/long-range threats',
      'Primary mission is supporting engaged units',
      'DNBI higher than combat casualties',
      'Mental health: operational stress, not combat trauma'
    ],
    
    // Applicable unit types
    applicable_unit_types: [
      'LOGISTICS',
      'MEDICAL', 
      'SIGNAL',
      'MAINTENANCE',
      'TRANSPORT',
      'HQ'
    ],
    
    romanian_term: 'Operații de Sprijin',
    
    teaching_points: [
      'Very low combat casualties (5% of combat units)',
      'Ukraine lesson: Rear areas still dangerous!',
      'Drone and long-range fire threats real',
      'DNBI is primary casualty source',
      'Support units dont use combat postures',
      'Posture determines realistic casualty expectation'
    ],
    
    ukraine_lessons: [
      'Logistics convoys heavily targeted by drones',
      'Fuel and ammunition depots priority targets',
      'Even medical units not immune despite protected status',
      'Signal units targeted for EW effects',
      'HQ elements precision-targeted'
    ]
  },
  
  RIVER_CROSSING: {
    value: 'RIVER_CROSSING',
    label: 'River Crossing Operation',
    icon: '🌊',
    category: 'TRANSITION',
    
    casualty_multiplier: 1.4,
    
    ukraine_data: {
      observed_multiplier: 1.5,
      sample_operations: ['Dnipro crossings 2023'],
      notes: 'River crossings extremely costly'
    },
    
    description: 'Assault or support crossing of water obstacle',
    
    medical: {
      operational: 'DELAYED',
      mtf_status: 'LIMITED',
      mtf_deployment_hours: { ROLE1: 1, ROLE2_BASIC: 4, ROLE2_ENHANCED: 12 },
      medical_delay_hours: 4,
      
      typical_paradigm: 'DENIED',
      evacuation_notes: 'Evacuation extremely difficult until bridgehead secured',
      
      golden_hour_probability: 0.15,
      
      expected_flow: 'VERY HIGH during crossing',
      mascal_risk: 'VERY_HIGH',
      pfc_likely: true,
      pfc_duration_hours: { min: 4, typical: 8, max: 24 }
    },
    
    characteristics: [
      'Extremely high risk operation',
      'Concentrated forces = concentrated casualties',
      'Medical delayed until bridgehead established',
      'Drowning casualties in addition to combat',
      'Equipment loss likely'
    ],
    
    timing: {
      crossing_phase_hours: { min: 4, typical: 8, max: 24 },
      consolidation_hours: { min: 12, typical: 24, max: 48 }
    },
    
    romanian_term: 'Forțarea Cursului de Apă',
    
    teaching_points: [
      'Highest casualty rate posture',
      'Medical essentially unavailable during crossing',
      'PFC ESSENTIAL - hours of delay expected',
      'Drowning additional casualty source',
      'Plan for 40%+ casualties in assault wave'
    ],
    
    ukraine_lessons: [
      'Dnipro crossings had 50%+ assault wave casualties',
      'Evacuation only possible at night',
      'Boat casualties often fatal (drowning)',
      'Forward surgical teams critical once across',
      'Blood products must cross early'
    ]
  },
  
  AIRBORNE_OPERATION: {
    value: 'AIRBORNE_OPERATION',
    label: 'Airborne/Air Assault Operation',
    icon: '🪂',
    category: 'TRANSITION',
    
    casualty_multiplier: 1.3,
    
    ukraine_data: {
      observed_multiplier: 1.8,
      sample_operations: ['Hostomel airport Feb 2022'],
      notes: 'Air assault into contested area extremely costly'
    },
    
    description: 'Vertical envelopment operation',
    
    medical: {
      operational: 'DELAYED',
      mtf_status: 'LIMITED',
      mtf_deployment_hours: { ROLE1: 2, ROLE2_BASIC: 12, ROLE2_ENHANCED: 24 },
      medical_delay_hours: 2,
      
      typical_paradigm: 'DENIED',
      evacuation_notes: 'Evacuation extremely limited until linkup',
      
      golden_hour_probability: 0.10,
      
      expected_flow: 'HIGH during insertion',
      mascal_risk: 'VERY_HIGH',
      pfc_likely: true,
      pfc_duration_hours: { min: 6, typical: 24, max: 72 }
    },
    
    characteristics: [
      'High risk insertion',
      'Limited medical initially',
      'Evacuation not possible until linkup',
      'Aircraft casualties often catastrophic',
      'Resupply challenging'
    ],
    
    romanian_term: 'Operație Aeropurtată',
    
    teaching_points: [
      'Very high initial casualties',
      'Medical severely limited until linkup',
      'PFC ESSENTIAL - 24+ hours possible',
      'Aircraft loss = mass casualty',
      'Blood products critical'
    ]
  },
  
  RESERVE: {
    value: 'RESERVE',
    label: 'Reserve (No Contact)',
    icon: '📦',
    category: 'NON_COMBAT',
    
    casualty_multiplier: 0.0,
    
    description: 'Out of contact, ready to be committed',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Medical assets rested, can support engaged units',
      
      golden_hour_probability: 1.0,
      
      expected_flow: 'Minimal (DNBI only)',
      mascal_risk: 'NONE',
      pfc_likely: false,
      
      can_support_others: true,
      overflow_capacity: 'Available',
      medical_staff_status: 'Rested',
      equipment_status: 'Maintained'
    },
    
    characteristics: [
      'Not in enemy contact',
      'No combat casualties',
      'Medical fully operational',
      'Can accept overflow from engaged units',
      'Medical staff rested'
    ],
    
    romanian_term: 'Rezervă',
    
    teaching_points: [
      'Zero combat casualties (DNBI only)',
      'Medical fully operational and rested',
      'Can accept overflow from engaged units',
      'Medical support available to others',
      'Casualty rate changes WHEN committed to combat'
    ]
  },
  
  RECONSTITUTION: {
    value: 'RECONSTITUTION',
    label: 'Reconstitution/Recovery',
    icon: '🔧',
    category: 'NON_COMBAT',
    
    casualty_multiplier: 0.0,
    
    description: 'Unit rebuilding after significant losses',
    
    medical: {
      operational: true,
      mtf_status: 'FULLY_OPERATIONAL',
      mtf_deployment_hours: 0,
      
      typical_paradigm: 'PERMISSIVE',
      evacuation_notes: 'Full medical support for recovery',
      
      golden_hour_probability: 1.0,
      
      expected_flow: 'RTD personnel returning',
      mascal_risk: 'NONE',
      pfc_likely: false,
      
      focus: 'RTD processing, rehabilitation'
    },
    
    characteristics: [
      'Unit rebuilding',
      'Receiving replacements',
      'RTD personnel returning',
      'Medical focus on rehabilitation',
      'Mental health critical'
    ],
    
    romanian_term: 'Reconstituire',
    
    teaching_points: [
      'No combat casualties',
      'Medical focus on RTD and rehabilitation',
      'Mental health support critical',
      'Opportunity for medical training',
      'Equipment maintenance'
    ]
  }
};

// ============================================================================
// COMBINED OPERATIONAL POSTURES
// ============================================================================

export const OPERATIONAL_POSTURES = {
  ...OFFENSIVE_POSTURES,
  ...DEFENSIVE_POSTURES,
  ...RETROGRADE_POSTURES,
  ...STABILITY_POSTURES
};

// ============================================================================
// POSTURE TRANSITIONS
// Expected casualties during posture changes
// ============================================================================

export const POSTURE_TRANSITIONS = {
  // From offensive to defensive
  ATTACK_TO_DEFENSE: {
    from: ['DELIBERATE_ATTACK', 'HASTY_ATTACK'],
    to: ['DELIBERATE_DEFENSE', 'HASTY_DEFENSE'],
    transition_multiplier: 1.1,
    transition_hours: 4,
    notes: 'Transition under fire increases casualties'
  },
  
  // From defensive to offensive
  DEFENSE_TO_ATTACK: {
    from: ['DELIBERATE_DEFENSE', 'HASTY_DEFENSE'],
    to: ['DELIBERATE_ATTACK', 'HASTY_ATTACK'],
    transition_multiplier: 1.0,
    transition_hours: 6,
    notes: 'Breakout from defense can be costly'
  },
  
  // To retrograde
  TO_RETROGRADE: {
    from: ['DELIBERATE_DEFENSE', 'HASTY_DEFENSE', 'HASTY_ATTACK'],
    to: ['DELAY', 'WITHDRAWAL'],
    transition_multiplier: 1.2,
    transition_hours: 2,
    notes: 'Disengagement under fire dangerous'
  },
  
  // From reserve to combat
  RESERVE_TO_COMBAT: {
    from: ['RESERVE'],
    to: ['HASTY_ATTACK', 'HASTY_DEFENSE'],
    transition_multiplier: 1.0,
    transition_hours: 0,
    notes: 'Commitment of reserve'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * BUG-045 FIX: Normalize posture value to string
 * 
 * Tab 2 saves posture as an object {value: 'DELIBERATE_DEFENSE', label: '...'}
 * but all posture lookup functions expect just the string 'DELIBERATE_DEFENSE'.
 * This helper extracts the string value from any input format.
 * 
 * STANDARDIZED DEFAULT: DELIBERATE_DEFENSE (0.6× casualty multiplier)
 * - Not too optimistic (RESERVE = 0.0×)
 * - Not too pessimistic (HASTY_DEFENSE = 0.85×)
 * - Consistent across all PSP/CRE modules
 * 
 * @param {string|Object} posture - Posture value (may be object or string)
 * @param {string} defaultPosture - Default if posture is invalid (default: 'DELIBERATE_DEFENSE')
 * @returns {string} Normalized posture string
 * 
 * @example
 * normalizePostureValue('ATTACK')                      // Returns 'ATTACK'
 * normalizePostureValue({value: 'ATTACK', label: 'X'}) // Returns 'ATTACK'
 * normalizePostureValue(undefined)                     // Returns 'DELIBERATE_DEFENSE'
 * normalizePostureValue({id: 'DEFEND'})                // Returns 'DEFEND'
 */
export function normalizePostureValue(posture, defaultPosture = 'DELIBERATE_DEFENSE') {
  // Case 1: Already a valid string
  if (typeof posture === 'string' && posture.length > 0) {
    // Verify it's a valid posture
    if (OPERATIONAL_POSTURES[posture]) {
      return posture;
    }
    // Try uppercase
    const upper = posture.toUpperCase();
    if (OPERATIONAL_POSTURES[upper]) {
      return upper;
    }
    // Unknown string - log warning and return default
    console.warn(`Unknown posture: ${posture}, defaulting to ${defaultPosture}`);
    return defaultPosture;
  }
  
  // Case 2: Object with various possible property names
  if (posture && typeof posture === 'object') {
    // Try .value first (MUI Select pattern, Tab 2 pattern)
    if (posture.value && typeof posture.value === 'string') {
      return normalizePostureValue(posture.value, defaultPosture);
    }
    // Try .id
    if (posture.id && typeof posture.id === 'string') {
      return normalizePostureValue(posture.id, defaultPosture);
    }
    // Try .key
    if (posture.key && typeof posture.key === 'string') {
      return normalizePostureValue(posture.key, defaultPosture);
    }
    // Try .type
    if (posture.type && typeof posture.type === 'string') {
      return normalizePostureValue(posture.type, defaultPosture);
    }
    // Object but no extractable value
    console.warn(`Unknown posture: [object Object], defaulting to ${defaultPosture}`);
    return defaultPosture;
  }
  
  // Case 3: null, undefined, or other invalid type
  if (posture !== undefined && posture !== null) {
    console.warn(`Unknown posture: ${posture}, defaulting to ${defaultPosture}`);
  }
  return defaultPosture;
}

/**
 * Get posture by value
 * BUG-045 FIX: Now normalizes input to handle object postures from Tab 2
 * 
 * @param {string|Object} value - Posture value (may be object or string)
 * @returns {Object} Posture configuration
 */
export function getPostureByValue(value) {
  // BUG-045 FIX: Normalize posture value before lookup
  const normalizedValue = normalizePostureValue(value, 'DELIBERATE_DEFENSE');
  return OPERATIONAL_POSTURES[normalizedValue] || OPERATIONAL_POSTURES.DELIBERATE_DEFENSE;
}

/**
 * Get posture casualty multiplier
 * @param {string} postureValue - Posture value
 * @returns {number} Casualty multiplier
 */
export function getPostureCasualtyMultiplier(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.casualty_multiplier;
}

/**
 * Get Ukraine-validated casualty multiplier
 * @param {string} postureValue - Posture value
 * @returns {number} Ukraine-validated multiplier
 */
export function getUkraineValidatedMultiplier(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.ukraine_data?.observed_multiplier || posture.casualty_multiplier;
}

/**
 * Get evacuation paradigm for posture
 * @param {string} postureValue - Posture value
 * @returns {string} Evacuation paradigm (PERMISSIVE/CONTESTED/DENIED)
 */
export function getEvacuationParadigm(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.medical?.typical_paradigm || 'CONTESTED';
}

/**
 * Get golden hour probability for posture
 * @param {string} postureValue - Posture value
 * @returns {number} Probability (0-1)
 */
export function getGoldenHourProbability(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.medical?.golden_hour_probability || 0.5;
}

/**
 * Check if medical is operational for this posture
 * @param {string} postureValue - Posture value
 * @returns {boolean|string} True, false, or 'DELAYED'
 */
export function isMedicalOperational(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.medical?.operational ?? true;
}

/**
 * Get MTF deployment time for posture
 * @param {string} postureValue - Posture value
 * @param {string} roleLevel - MTF role level (ROLE1, ROLE2_BASIC, etc.)
 * @returns {number} Hours to deploy (0 if already operational)
 */
export function getMTFDeploymentTime(postureValue, roleLevel) {
  const posture = getPostureByValue(postureValue);
  
  const deploymentHours = posture.medical?.mtf_deployment_hours;
  
  if (deploymentHours === 0) return 0;
  
  if (typeof deploymentHours === 'object') {
    return deploymentHours[roleLevel] || 0;
  }
  
  return deploymentHours || 0;
}

/**
 * Get medical delay hours (for special operations)
 * @param {string} postureValue - Posture value
 * @returns {number} Medical delay in hours
 */
export function getMedicalDelayHours(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.medical?.medical_delay_hours || 0;
}

/**
 * Check if PFC is likely for posture
 * @param {string} postureValue - Posture value
 * @returns {boolean} True if PFC likely
 */
export function isPFCLikely(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.medical?.pfc_likely || false;
}

/**
 * Get MASCAL risk level for posture
 * @param {string} postureValue - Posture value
 * @returns {string} Risk level (NONE/LOW/MODERATE/HIGH/VERY_HIGH)
 */
export function getMASCALRisk(postureValue) {
  const posture = getPostureByValue(postureValue);
  return posture.medical?.mascal_risk || 'LOW';
}

/**
 * Get postures as array (for dropdowns)
 * @returns {Array} Array of posture objects
 */
export function getPosturesArray() {
  return Object.values(OPERATIONAL_POSTURES).map(p => ({
    value: p.value,
    label: p.label,
    icon: p.icon,
    category: p.category,
    casualty_multiplier: p.casualty_multiplier
  }));
}

/**
 * Get postures by category
 * @param {string} category - OFFENSIVE, DEFENSIVE, RETROGRADE, etc.
 * @returns {Array} Filtered postures
 */
export function getPosturesByCategory(category) {
  return Object.values(OPERATIONAL_POSTURES).filter(p => p.category === category);
}

/**
 * Calculate combined posture multiplier for nested units
 * Most specific level (battalion) overrides higher echelons
 * 
 * @param {string} divisionPosture - Division level posture
 * @param {string} brigadePosture - Brigade level posture (overrides if different)
 * @param {string} battalionPosture - Battalion level posture (overrides if different)
 * @returns {number} Effective casualty multiplier
 */
export function calculateNestedPostureMultiplier(divisionPosture, brigadePosture = null, battalionPosture = null) {
  // Use most specific posture available
  const effectivePosture = battalionPosture || brigadePosture || divisionPosture;
  return getPostureCasualtyMultiplier(effectivePosture);
}

/**
 * Calculate posture modifier with all medical factors
 * @param {string} postureValue - Posture value
 * @returns {Object} Complete posture modifier data
 */
export function calculatePostureModifier(postureValue) {
  const posture = getPostureByValue(postureValue);
  
  return {
    posture: posture.label,
    category: posture.category,
    
    casualty: {
      multiplier: posture.casualty_multiplier,
      ukraine_validated: posture.ukraine_data?.observed_multiplier || posture.casualty_multiplier
    },
    
    medical: {
      operational: posture.medical?.operational ?? true,
      mtf_status: posture.medical?.mtf_status || 'OPERATIONAL',
      paradigm: posture.medical?.typical_paradigm || 'CONTESTED',
      golden_hour: posture.medical?.golden_hour_probability || 0.5,
      pfc_likely: posture.medical?.pfc_likely || false,
      mascal_risk: posture.medical?.mascal_risk || 'LOW'
    },
    
    notes: posture.evacuation_notes || posture.medical?.evacuation_notes
  };
}

/**
 * Get posture for calculator integration
 * @param {string} postureValue - Posture value
 * @returns {Object} Calculator-ready data
 */
export function getPostureForCalculator(postureValue) {
  const posture = getPostureByValue(postureValue);
  
  return {
    casualty_multiplier: posture.casualty_multiplier,
    evacuation_paradigm: posture.medical?.typical_paradigm || 'CONTESTED',
    golden_hour_probability: posture.medical?.golden_hour_probability || 0.5,
    mtf_operational: posture.medical?.operational ?? true,
    mtf_status: posture.medical?.mtf_status || 'OPERATIONAL',
    pfc_required: posture.medical?.pfc_likely || false,
    medical_delay_hours: posture.medical?.medical_delay_hours || 0
  };
}

/**
 * Validate posture value
 * @param {string} value - Posture value to validate
 * @returns {boolean} True if valid
 */
export function isValidPosture(value) {
  return value in OPERATIONAL_POSTURES;
}

/**
 * Get posture transition data
 * @param {string} fromPosture - Starting posture
 * @param {string} toPosture - Ending posture
 * @returns {Object|null} Transition data or null
 */
export function getPostureTransition(fromPosture, toPosture) {
  for (const [key, transition] of Object.entries(POSTURE_TRANSITIONS)) {
    if (transition.from.includes(fromPosture) && transition.to.includes(toPosture)) {
      return {
        name: key,
        ...transition
      };
    }
  }
  return null;
}

/**
 * Calculate transition casualties
 * @param {string} fromPosture - Starting posture
 * @param {string} toPosture - Ending posture
 * @param {number} strength - Unit strength
 * @param {number} baseRate - Base casualty rate
 * @returns {Object} Transition casualty estimate
 */
export function calculateTransitionCasualties(fromPosture, toPosture, strength, baseRate) {
  const transition = getPostureTransition(fromPosture, toPosture);
  
  if (!transition) {
    return {
      casualties: 0,
      hours: 0,
      notes: 'Direct transition, no additional casualties'
    };
  }
  
  const transitionCasualties = Math.round(
    strength * baseRate * transition.transition_multiplier * (transition.transition_hours / 24)
  );
  
  return {
    casualties: transitionCasualties,
    hours: transition.transition_hours,
    multiplier: transition.transition_multiplier,
    notes: transition.notes
  };
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const POSTURE_EDUCATIONAL = {
  key_principles: [
    'Posture determines base casualty multiplier',
    'Defensive postures have 30-50% lower casualties',
    'Special operations (river crossing, airborne) have highest casualties',
    'Medical capability varies significantly by posture',
    'Nested postures: Battalion overrides Brigade overrides Division'
  ],
  
  ukraine_lessons: [
    'Prepared defense (Surovikin Line) dramatically reduces casualties',
    'River crossings 50%+ assault wave casualties',
    'Hasty attacks without artillery prep very costly',
    'Night operations reduce drone vulnerability',
    'Orderly withdrawal preserves force'
  ],
  
  medical_implications: [
    'Golden hour probability ranges from 10% (airborne) to 90% (stability)',
    'PFC required for all special operations postures',
    'MASCAL risk highest during river crossing and airborne ops',
    'Reserve units can provide medical overflow capacity'
  ],
  
  common_errors: [
    'Not adjusting medical positioning for posture change',
    'Underestimating special operations casualties',
    'Assuming rear areas safe in any posture',
    'Not planning PFC for high-risk postures'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const posturesModule = {
  OPERATIONAL_POSTURES,
  POSTURE_CATEGORIES,
  OFFENSIVE_POSTURES,
  DEFENSIVE_POSTURES,
  RETROGRADE_POSTURES,
  STABILITY_POSTURES,
  POSTURE_TRANSITIONS,
  normalizePostureValue,  // BUG-045 FIX: Export normalizer
  getPostureByValue,
  getPostureCasualtyMultiplier,
  getUkraineValidatedMultiplier,
  getEvacuationParadigm,
  getGoldenHourProbability,
  isMedicalOperational,
  getMTFDeploymentTime,
  getMedicalDelayHours,
  isPFCLikely,
  getMASCALRisk,
  getPosturesArray,
  getPosturesByCategory,
  calculateNestedPostureMultiplier,
  calculatePostureModifier,
  getPostureForCalculator,
  isValidPosture,
  getPostureTransition,
  calculateTransitionCasualties,
  POSTURE_EDUCATIONAL
};

export default posturesModule;
